import { Clipboard } from '@angular/cdk/clipboard';
import { inject, Injectable } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { ModalService, ToastService } from 'mbs-ui-kit';
import { BrandsModalComponent } from 'mbs-ui-shared@components/download-build/brands-modal/brands-modal.component';
import {
  BuildAction,
  buildsThatSupportBranding,
  buildsThatSupportLinuxDebian,
  buildsThatSupportLinuxRedHat,
  installRmmAgentMsiScriptUrl,
  InstallWindowsAgentCmdName
} from 'mbs-ui-shared@components/download-build/download-build.constants';
import { MsiDownloadInfoModalComponent } from 'mbs-ui-shared@components/download-build/msi-download-info-modal/msi-download-info-modal.component';
import {
  UpdateRecommendedAnswer,
  UpdateRecommendedModalComponent
} from 'mbs-ui-shared@components/update-recommended-modal/update-recommended-modal.component';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { BuildType } from 'mbs-ui-shared@models';
import Brand from 'mbs-ui-shared@models/Brand';
import { Build, BuildOsType, BuildVersionType, hasRevokedCertificate } from 'mbs-ui-shared@models/build';
import { CommonResponseType } from 'mbs-ui-shared@services/build-service/build-service.types';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { catchError, EMPTY, filter, from, map, Observable, of, switchMap, take, tap } from 'rxjs';

export type BuildDownloadOptions = {
  companyId?: string;
  isSandboxEnabled?: boolean;
  licensedBrandList?: Brand[];
};

@Injectable({ providedIn: 'root' })
export class BuildDownloadService {
  private readonly auth = inject(AuthFacade);
  private readonly buildService = inject(BuildService);
  private readonly buildsFacade = inject(BuildsFacade);
  private readonly clipboard = inject(Clipboard);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);

  public getPreparedLink(link: string, { companyId, brandId }: { brandId?: string; companyId?: string } = {}): string {
    if (!(companyId || brandId) || !link) return link;

    const url = new URL(link);

    /* Ignore brandId if companyId exists */
    if (companyId) {
      url.searchParams.append('companyId', companyId);
    } else {
      url.searchParams.append('brandId', brandId);
    }

    return url.toString();
  }

  public getInstallCommand(build: Build, link: string, companyId?: string): string {
    if (build.buildOs === BuildOsType.Windows) {
      return build.mbsBuildType === BuildType.RMMAgentMsi
        ? this.getCommandWindowsRmmMsi(link, companyId)
        : this.getCommandWindows(link, this.getCmdName(build.mbsBuildType));
    }

    if (build.buildOs === BuildOsType.Mac || build.buildOs === BuildOsType.MacArm) return this.getCommandMac(link);

    if (buildsThatSupportLinuxRedHat.includes(build.mbsBuildType)) return this.getCommandLinuxRedhat(link);

    if (buildsThatSupportLinuxDebian.includes(build.mbsBuildType)) return this.getCommandLinuxDebian(link);

    return undefined;
  }

  public download(build: Build, versionType: BuildVersionType, options: BuildDownloadOptions = {}): Observable<unknown> {
    if (build.mbsBuildType === BuildType.RMMAgentMsi) {
      return this.confirmMsiDownload(options).pipe(switchMap(() => this.startDownload(build, versionType, options)));
    }

    return this.startDownload(build, versionType, options);
  }

  public copyLink(build: Build, versionType: BuildVersionType, options: BuildDownloadOptions = {}): Observable<unknown> {
    return this.run(build, versionType, BuildAction.CopyLink, options);
  }

  public copyCommand(build: Build, versionType: BuildVersionType, options: BuildDownloadOptions = {}): Observable<unknown> {
    return this.run(build, versionType, BuildAction.CopyCommand, options);
  }

  public generateBuild(build: Build, isBuildDestinationSandbox = false): Observable<CommonResponseType> {
    return this.buildService
      .requestBuild({ buildsTypeList: [build.mbsBuildType], isBuildDestinationSandbox })
      .pipe(tap((res) => this.handleResponse(res)));
  }

  // Private section
  private startDownload(build: Build, versionType: BuildVersionType, options: BuildDownloadOptions): Observable<unknown> {
    if (versionType === BuildVersionType.Public && hasRevokedCertificate(build.mbsBuildType, build.public?.version)) {
      return this.suggestUpToDateBuild(build, options);
    }

    return this.run(build, versionType, BuildAction.Download, options);
  }

  private confirmMsiDownload({ companyId }: BuildDownloadOptions): Observable<unknown> {
    const command = this.getMsiexecInstallCommand(companyId);

    return from(this.modalService.openCustom(MsiDownloadInfoModalComponent, { data: { command } })).pipe(catchError(() => EMPTY));
  }

  private run(build: Build, versionType: BuildVersionType, action: BuildAction, options: BuildDownloadOptions): Observable<unknown> {
    const brands = options.licensedBrandList ?? [];

    if (!brands.length || !buildsThatSupportBranding.includes(build.mbsBuildType) || this.needToTakeCommonBuild(options)) {
      this.apply(build, versionType, action, options.companyId ? { companyId: options.companyId } : {});

      return of(null);
    }

    if (this.needToTakeAdvancedBuild(brands)) {
      this.apply(build, versionType, action, { brandId: brands.find(this.isBrandAdvancedAndDefault).id });

      return of(null);
    }

    return this.openBrandsModal(build, versionType, action, brands);
  }

  // The brands modal is the only path that toasts, matching what the downloads sidepanel has always done
  private openBrandsModal(build: Build, versionType: BuildVersionType, action: BuildAction, brands: Brand[]): Observable<unknown> {
    const modal = this.modalService.openRef(BrandsModalComponent, { data: { action, licensedBrandList: brands } });

    return from<Promise<string>>(modal.result).pipe(
      catchError(() => EMPTY),
      tap((brandId: string) => {
        this.apply(build, versionType, action, { brandId }, action === BuildAction.Download);

        if (action !== BuildAction.Download) this.toastService.success();
      })
    );
  }

  private apply(
    build: Build,
    versionType: BuildVersionType,
    action: BuildAction,
    linkOptions: { brandId?: string; companyId?: string },
    openInNewTab = false
  ): void {
    const link = this.getPreparedLink(build[versionType]?.downloadLink, linkOptions);

    switch (action) {
      case BuildAction.Download:
        openInNewTab ? window.open(link, '_blank') : window.top.open(link);
        break;
      case BuildAction.CopyLink:
        this.clipboard.copy(link);
        break;
      case BuildAction.CopyCommand:
        link && this.clipboard.copy(this.getInstallCommand(build, link, linkOptions.companyId));
    }
  }

  private suggestUpToDateBuild(build: Build, options: BuildDownloadOptions): Observable<unknown> {
    const canPublishSandbox =
      options.isSandboxEnabled && !!build.sandbox && !hasRevokedCertificate(build.mbsBuildType, build.sandbox.version);
    const question = this.i18nPipe.transform(
      canPublishSandbox
        ? 'shared:download-build.updateRecommended.makePublicQuestion'
        : 'shared:download-build.updateRecommended.generateQuestion'
    );

    return from(this.modalService.openCustom<UpdateRecommendedAnswer>(UpdateRecommendedModalComponent, { data: { question } })).pipe(
      // The cross rejects, so closing the modal leaves the download untouched
      catchError(() => of(null)),
      take(1),
      switchMap((answer) => {
        if (answer === UpdateRecommendedAnswer.No) return this.run(build, BuildVersionType.Public, BuildAction.Download, options);

        if (answer !== UpdateRecommendedAnswer.Yes) return EMPTY;

        return canPublishSandbox ? this.makeBuildPublicAndDownload(build, options) : this.generateBuild(build, options.isSandboxEnabled);
      })
    );
  }

  private makeBuildPublicAndDownload(build: Build, options: BuildDownloadOptions): Observable<unknown> {
    return this.buildsFacade.makePublic(build).pipe(
      tap((res) => this.handleResponse(res)),
      switchMap((res) => (res?.success ? this.whenBuildPublished(build) : EMPTY)),
      switchMap((published) => this.run(published, BuildVersionType.Public, BuildAction.Download, options))
    );
  }

  // makePublic only starts the publishing, the download link stays on the revoked build until the store catches up
  private whenBuildPublished(build: Build): Observable<Build> {
    return this.buildsFacade.builds$.pipe(
      map((builds) => builds?.find(({ mbsBuildType }) => mbsBuildType === build.mbsBuildType)),
      filter((updated) => !!updated && !hasRevokedCertificate(updated.mbsBuildType, updated.public?.version)),
      take(1)
    );
  }

  private needToTakeCommonBuild({ companyId, licensedBrandList = [] }: BuildDownloadOptions): boolean {
    if (companyId) return true;

    if (licensedBrandList.length === 2) return !licensedBrandList.some(this.isBrandAdvancedAndDefault);

    return licensedBrandList.length === 1 && licensedBrandList[0].isBase;
  }

  private needToTakeAdvancedBuild(licensedBrandList: Brand[]): boolean {
    return licensedBrandList.length === 2 && licensedBrandList.some(this.isBrandAdvancedAndDefault);
  }

  private isBrandAdvancedAndDefault = (brand: Brand): boolean => !brand.isBase && brand.isDefault;

  private handleResponse(res: { message?: string; success?: boolean }): void {
    this.toastService[res?.success ? 'success' : 'error'](res?.message);
  }

  private getCmdName(buildType: BuildType): InstallWindowsAgentCmdName {
    switch (buildType) {
      case BuildType.RMMAgentExe:
        return InstallWindowsAgentCmdName.RMM;
      case BuildType.CBLRA:
        return InstallWindowsAgentCmdName.Connect;
      default:
        return InstallWindowsAgentCmdName.Common;
    }
  }

  private getCommandWindowsRmmMsi(link: string, companyId?: string): string {
    const ownerId = this.auth.currentUserValue?.ProviderInfo?.Id ?? '';
    const companyIdParam = companyId ? ` -CompanyID '${companyId}'` : '';

    return `powershell -ExecutionPolicy Bypass -command "& {[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12; iex (New-Object System.Net.WebClient).DownloadString('${installRmmAgentMsiScriptUrl}'); Install-RMMAgentMSI -URL '${link}' -OwnerID '${ownerId}'${companyIdParam} -Force}"`;
  }

  private getMsiexecInstallCommand(companyId?: string): string {
    const ownerId = this.auth.currentUserValue?.ProviderInfo?.Id ?? '';
    const companyIdParam = companyId ? ` COMPANY_ID="${companyId}"` : '';

    return `msiexec /i "RMMAgent.msi" OWNER_ID="${ownerId}"${companyIdParam}`;
  }

  private getCommandWindows(link: string, installAgentName: InstallWindowsAgentCmdName): string {
    return `powershell -ExecutionPolicy Bypass -command "& {[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12; iex (New-Object System.Net.WebClient).DownloadString('https://git.io/JUSAA'); Install-MSP360Module; Install-${installAgentName} -URL '${link}' -Force}"`;
  }

  private getCommandLinuxDebian(link: string): string {
    return `sudo curl "${origin}/Install/install_deb.sh" -L | sudo bash -s "${link}"`;
  }

  private getCommandLinuxRedhat(link: string): string {
    return `sudo curl "${origin}/Install/install_rpm.sh" -L | sudo bash -s "${link}"`;
  }

  private getCommandMac(link: string): string {
    return `sudo curl "${origin}/Install/install_mac.sh" -L | sudo bash -s "${link}"`;
  }
}
